import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/api';
import { logger } from '../../utils/logger';
import { SavedSettingCreate, SavedCharacterCreate } from '../../types/workshop';
import '../../styles/components/SaveToWorkshop.css';

interface SaveToWorkshopProps {
  setting?: string;
  characters?: string[];
  onSuccess?: () => void;
}

/**
 * Component for saving story settings and characters to the workshop
 */
const SaveToWorkshop: React.FC<SaveToWorkshopProps> = ({
  setting,
  characters = [],
  onSuccess
}) => {
  const [activeTab, setActiveTab] = useState<'setting' | 'character'>(setting ? 'setting' : 'character');
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Setting form state
  const [settingName, setSettingName] = useState('');
  const [settingDescription, setSettingDescription] = useState('');
  const [selectedSetting, setSelectedSetting] = useState('');
  
  // Character form state
  const [characterName, setCharacterName] = useState('');
  const [characterDescription, setCharacterDescription] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState('');

  // Parse the setting on component mount and when it changes
  useEffect(() => {
    if (setting) {
      // Check if the setting is in the enhanced format: "Name: Details (Description)"
      const colonIndex = setting.indexOf(':');
      if (colonIndex > 0) {
        // This is likely an enhanced setting
        // Extract just the details part (after the colon)
        const settingDetails = setting.substring(colonIndex + 1).trim();
        
        // Further parse to remove any description in parentheses if present
        const parenIndex = settingDetails.lastIndexOf('(');
        if (parenIndex > 0) {
          setSelectedSetting(settingDetails.substring(0, parenIndex).trim());
        } else {
          setSelectedSetting(settingDetails);
        }
      } else {
        // This is a simple setting string, use as is
        setSelectedSetting(setting);
      }
    }
  }, [setting]);

  // Show the form for saving
  const handleShowForm = (type: 'setting' | 'character') => {
    setActiveTab(type);
    setShowForm(true);
    setSuccess(false);
    setError(null);
    
    if (type === 'character' && characters.length > 0) {
      // Extract character data
      const character = characters[0];
      // Check if the character is in the enhanced format: "Name: Details (Description)"
      const colonIndex = character.indexOf(':');
      if (colonIndex > 0) {
        // This is likely an enhanced character
        // Extract just the details part (after the colon)
        const characterDetails = character.substring(colonIndex + 1).trim();
        
        // Further parse to remove any description in parentheses if present
        const parenIndex = characterDetails.lastIndexOf('(');
        if (parenIndex > 0) {
          setSelectedCharacter(characterDetails.substring(0, parenIndex).trim());
        } else {
          setSelectedCharacter(characterDetails);
        }
      } else {
        // This is a simple character string, use as is
        setSelectedCharacter(character);
      }
    }
  };

  // Handle saving a setting
  const handleSaveSetting = async () => {
    if (!settingName.trim() || !selectedSetting.trim()) {
      setError('Please provide a name and details for your setting.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const settingData: SavedSettingCreate = {
        name: settingName.trim(),
        setting: selectedSetting.trim(),
        description: settingDescription.trim() || undefined
      };
      
      await ApiService.createSavedSetting(settingData);
      
      setSuccess(true);
      setShowForm(false);
      setSettingName('');
      setSettingDescription('');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error saving setting:', err);
      setError('Failed to save your setting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle saving a character
  const handleSaveCharacter = async () => {
    if (!characterName.trim() || !selectedCharacter.trim()) {
      setError('Please provide a name and details for your character.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await ApiService.createSavedCharacter(
        characterName.trim(),
        selectedCharacter.trim(),
        characterDescription.trim() || undefined
      );
      
      setSuccess(true);
      setShowForm(false);
      setCharacterName('');
      setCharacterDescription('');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error saving character:', err);
      setError('Failed to save your character. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setError(null);
  };

  return (
    <div className="save-to-workshop">
      <h2 className="save-to-workshop-title">Save to Workshop</h2>
      
      {success && (
        <div className="save-success-message">
          <p>Successfully saved to your workshop!</p>
          <button 
            className="action-button primary"
            onClick={() => setSuccess(false)}
          >
            Save Another
          </button>
        </div>
      )}
      
      {!success && !showForm && (
        <div className="save-options">
          {setting && (
            <button 
              className="save-option-button setting-button"
              onClick={() => handleShowForm('setting')}
            >
              <span className="save-option-icon">🏞️</span>
              <span className="save-option-text">Save Setting</span>
            </button>
          )}
          
          {characters.length > 0 && (
            <button 
              className="save-option-button character-button"
              onClick={() => handleShowForm('character')}
            >
              <span className="save-option-icon">👥</span>
              <span className="save-option-text">Save Character</span>
            </button>
          )}
        </div>
      )}
      
      {showForm && activeTab === 'setting' && (
        <div className="save-form">
          <h3>Save Setting</h3>
          
          {error && <div className="save-error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="setting-name">Setting Name</label>
            <input 
              type="text" 
              id="setting-name" 
              value={settingName}
              onChange={(e) => setSettingName(e.target.value)}
              placeholder="e.g., Enchanted Forest"
              className="form-input"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="setting-description">Description (Optional)</label>
            <input 
              type="text" 
              id="setting-description" 
              value={settingDescription}
              onChange={(e) => setSettingDescription(e.target.value)}
              placeholder="Brief description of this setting"
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="setting-details">Setting Details</label>
            <textarea 
              id="setting-details" 
              value={selectedSetting}
              onChange={(e) => setSelectedSetting(e.target.value)}
              className="form-textarea"
              rows={4}
              required
            />
          </div>
          
          <div className="form-actions">
            <button 
              className="action-button secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className="action-button primary"
              onClick={handleSaveSetting}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Setting'}
            </button>
          </div>
        </div>
      )}
      
      {showForm && activeTab === 'character' && (
        <div className="save-form">
          <h3>Save Character</h3>
          
          {error && <div className="save-error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="character-name">Character Name</label>
            <input 
              type="text" 
              id="character-name" 
              value={characterName}
              onChange={(e) => setCharacterName(e.target.value)}
              placeholder="e.g., Luna the Unicorn"
              className="form-input"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="character-description">Description (Optional)</label>
            <input 
              type="text" 
              id="character-description" 
              value={characterDescription}
              onChange={(e) => setCharacterDescription(e.target.value)}
              placeholder="Brief description of this character"
              className="form-input"
            />
          </div>
          
          {characters.length > 1 && (
            <div className="form-group">
              <label htmlFor="character-select">Select Character</label>
              <select 
                id="character-select" 
                value={selectedCharacter}
                onChange={(e) => setSelectedCharacter(e.target.value)}
                className="form-select"
                required
              >
                {characters.map((char, index) => (
                  <option key={index} value={char}>
                    {char.length > 50 ? `${char.substring(0, 50)}...` : char}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="character-details">Character Details</label>
            <textarea 
              id="character-details" 
              value={selectedCharacter}
              onChange={(e) => setSelectedCharacter(e.target.value)}
              className="form-textarea"
              rows={4}
              required
            />
          </div>
          
          <div className="form-actions">
            <button 
              className="action-button secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className="action-button primary"
              onClick={handleSaveCharacter}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Character'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaveToWorkshop; 